import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  useLiteNotifications,
  markLiteNotificationRead,
  markAllLiteNotificationsRead,
} from "@/features/notifications/liteQueries";
import { queryClient } from "@/lib/queryClient";
import { getPriorityColor, getTimeAgo } from "@/store/lite/notificationsStore";
import { useReportsStore, STALE_REPORT_MS } from "@/store/reports/reportsStore";

/**
 * Lo que pide atención.
 *
 * ── Por qué sale del menú de aplicaciones ───────────────────────────────────
 * Vivía dentro del cajón de apps, como primer acordeón. Dos problemas.
 *
 * El primero: mezcla cosas de distinta naturaleza. Las aplicaciones son
 * destinos —una lista estable que no cambia sola—; un aviso es un estado que
 * aparece sin que nadie lo pida. Una lista estable con algo vivo dentro se
 * siente inestable, y lo vivo se pierde entre lo que no cambia.
 *
 * El segundo, y el que de verdad costaba: para enterarte tenías que abrir el
 * cajón. Fuera sólo había un punto rojo de 12 px SIN número, pegado al avatar,
 * así que un aviso y cuarenta se veían igual. Un aviso que hay que ir a buscar
 * llega tarde por definición.
 *
 * Ahora cuelga de su propia campana, que lleva el número a la vista.
 */

export default function MenuDeAvisos({ onCerrar }: { onCerrar: () => void }) {
  const { t } = useTranslation("common");
  const {
    items: avisos,
    unreadCount,
    isLoading,
    isFetchingNextPage,
    hasMore,
    fetchNextPage,
  } = useLiteNotifications();
  const { reports, getReportsOfUser, hasPendingReports, startPolling, stopPolling } =
    useReportsStore();

  /*
    Al abrir se refresca: si no hay push habilitadas, un GET es la única forma
    de enterarse de lo que llegó mientras el menú estaba cerrado.
  */
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["liteNotifications"] });
    getReportsOfUser().then(() => {
      if (hasPendingReports()) startPolling();
    });
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Un reporte lleva más de 15 min sin latido: el watchdog lo va a marcar como
  // fallido. Mostrarlo como «generando» sería mentir.
  const enCurso = useMemo(() => {
    const ahora = Date.now();
    return reports.filter((r) => {
      if (r.status !== "pending" && r.status !== "processing") return false;
      const ts = r.updatedAt ?? r.createdAt;
      const ms = ts ? ts._seconds * 1000 : 0;
      return ms === 0 || ahora - ms < STALE_REPORT_MS;
    });
  }, [reports]);

  const vacio = !isLoading && avisos.length === 0 && enCurso.length === 0;

  const abrir = async (aviso: (typeof avisos)[number]) => {
    if (!aviso.isRead) await markLiteNotificationRead(aviso.id);
    const destino = aviso.url || "/admin/notifications";
    const externo =
      destino.startsWith("http") && !destino.startsWith(window.location.origin);
    onCerrar();
    if (externo) {
      window.open(destino, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = destino.startsWith("http")
        ? new URL(destino).pathname
        : destino;
    }
  };

  return (
    <>
      <header className="flex shrink-0 items-center justify-between gap-2 px-4 py-3">
        <h2 className="hud-display truncate">{t("notifications.title", "Avisos")}</h2>
        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllLiteNotificationsRead()}
            className="shrink-0 rounded-sm px-2 py-1 text-[11px] text-primary-500 transition-colors hover:bg-subtle"
          >
            {t("notifications.markAllRead", "Marcar todo leído")}
          </button>
        )}
      </header>
      <div className="hud-filo h-px shrink-0" aria-hidden="true" />

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && (
          <p className="px-4 py-8 text-center text-xs text-content-muted">
            {t("notifications.loading")}
          </p>
        )}

        {vacio && (
          <div className="px-4 py-10 text-center">
            <i
              className="ri-notification-off-line text-2xl text-content-faint"
              aria-hidden="true"
            />
            <p className="mt-2 text-xs text-content-muted">{t("notifications.empty")}</p>
          </div>
        )}

        {enCurso.map((r) => (
          <div
            key={`rep-${r.reportId}`}
            className="flex items-start gap-3 border-b border-line-soft px-4 py-3"
          >
            <i
              className={`mt-0.5 shrink-0 text-[15px] ${
                r.status === "processing"
                  ? "ri-loader-4-line animate-spin text-info"
                  : "ri-time-line text-warning"
              }`}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-content">
                {t("notifications.report", { type: r.typeReport })}
              </span>
              <span className="block truncate text-[11px] text-content-muted">
                {r.status === "processing"
                  ? t("notifications.generating")
                  : t("notifications.queued")}
              </span>
            </span>
          </div>
        ))}

        {avisos.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => abrir(a)}
            className="flex w-full items-start gap-3 border-b border-line-soft px-4 py-3 text-left transition-colors hover:bg-subtle"
          >
            {/*
              El punto de prioridad va acompañado del tiempo y, si no está
              leído, de un nombre: el color solo no dice nada a quien no lo
              distingue.
            */}
            <span
              className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                a.isRead ? "bg-content-faint/40" : getPriorityColor(a.priority)
              }`}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">
              <span
                className={`block truncate text-sm ${
                  a.isRead ? "text-content-secondary" : "font-semibold text-content"
                }`}
              >
                {a.title}
              </span>
              {a.description && (
                <span className="mt-0.5 line-clamp-2 block text-[11px] leading-snug text-content-muted">
                  {a.description}
                </span>
              )}
              <span className="hud-dato mt-1 block text-[10px] text-content-faint">
                {getTimeAgo(a.createdAt)}
                {!a.isRead && ` · ${t("notifications.new")}`}
              </span>
            </span>
          </button>
        ))}

        {hasMore && !isFetchingNextPage && (
          <button
            type="button"
            onClick={() => fetchNextPage()}
            className="min-h-[44px] w-full text-xs text-primary-500 transition-colors hover:bg-subtle"
          >
            {t("notifications.loadMore", "Ver más")}
          </button>
        )}
        {isFetchingNextPage && (
          <p className="py-3 text-center text-xs text-content-muted">
            {t("notifications.loadingMore")}
          </p>
        )}
      </div>
    </>
  );
}
